import { AppName } from 'src/global/globalConfig';
import analyticsTracking from 'src/services/analytics-service';
import { AnalyticsOptions } from 'src/shared/helpers/analyticsHelper';

type TrackSingleDownloadParams = {
    fileId?: string;
    fileName?: string;
};

type TrackMultipleDownloadParams = {
    count: number;
};

type TrackUploadSuccessParams = {
    count: number;
};

type TrackUploadErrorParams = {
    message?: string;
};

type TrackRenameParams = {
    oldName?: string;
    newName: string;
};

type TrackDeleteParams = {
    fileId?: string;
    fileName?: string;
};

const usePortfolioFilesAnalytics = () => {
    const sendTrackEvent = (ga: Omit<AnalyticsOptions, 'category'>) => {
        analyticsTracking.trackEvent({
            ...ga,
            category: 'Portfolio files',
            app: AppName.PORTFOLIO_FILES,
        });
    };

    const trackSingleFileDownload = (params: TrackSingleDownloadParams) => {
        const labelParts: string[] = [];
        if (params.fileId) {
            labelParts.push(`id:${params.fileId}`);
        }
        if (params.fileName) {
            labelParts.push(`name:${params.fileName}`);
        }
        const label = labelParts.join('|');

        sendTrackEvent({
            action: 'portfolio_files_single_download_click',
            label,
        });
    };

    const trackMultipleFilesDownload = (params: TrackMultipleDownloadParams) => {
        sendTrackEvent({
            action: 'portfolio_files_bulk_download_click',
            label: `count:${params.count}`,
            value: params.count,
        });
    };

    const trackUploadSuccess = (params: TrackUploadSuccessParams) => {
        sendTrackEvent({
            action: 'portfolio_files_upload_success',
            label: `count:${params.count}`,
            value: params.count,
        });
    };

    const trackUploadError = (params: TrackUploadErrorParams) => {
        sendTrackEvent({
            action: 'portfolio_files_upload_error',
            label: params.message || 'unknown_error',
        });
    };

    const trackRename = (params: TrackRenameParams) => {
        const label = params.oldName ? `${params.oldName} -> ${params.newName}` : params.newName;
        sendTrackEvent({
            action: 'portfolio_files_rename',
            label,
        });
    };

    const trackDelete = (params: TrackDeleteParams) => {
        const labelParts: string[] = [];
        if (params.fileId) {
            labelParts.push(`id:${params.fileId}`);
        }
        if (params.fileName) {
            labelParts.push(`name:${params.fileName}`);
        }
        const label = labelParts.join('|');

        sendTrackEvent({
            action: 'portfolio_files_delete',
            label,
        });
    };

    return {
        trackSingleFileDownload,
        trackMultipleFilesDownload,
        trackUploadSuccess,
        trackUploadError,
        trackRename,
        trackDelete,
    };
};

export default usePortfolioFilesAnalytics;
